import { useState, useRef, useCallback, useEffect } from "react";
import { apiClient } from "../App";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Search, Loader2, MapPin } from "lucide-react";

// OCI V2.35 — shared GetAddress typeahead + structured manual address.
// Used by both the authenticated ConversionForm and the referral/self-complete page
// so they capture the SAME canonical address model. Backend proxies GetAddress (key
// never reaches the browser); falls back to safe mocked suggestions in preview.
export function composeAddress(v) {
  return [v.addressLine1, v.addressLine2, v.townCity, v.county]
    .map((p) => (p || "").trim()).filter(Boolean).join(", ");
}

export default function AddressCapture({ value, onChange, errors = {}, required = true, idPrefix = "addr" }) {
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [searching, setSearching] = useState(false);
  const [resolving, setResolving] = useState(false);
  const debounceRef = useRef(null);

  const search = useCallback(async (q) => {
    if (!q || q.trim().length < 2) { setSuggestions([]); setShowDropdown(false); return; }
    setSearching(true);
    try {
      const res = await apiClient.get(`/address/autocomplete?q=${encodeURIComponent(q.trim())}`);
      setSuggestions(res.data?.suggestions || []);
      setShowDropdown((res.data?.suggestions || []).length > 0);
    } catch {
      setSuggestions([]); setShowDropdown(false);
    } finally {
      setSearching(false);
    }
  }, []);

  useEffect(() => () => debounceRef.current && clearTimeout(debounceRef.current), []);

  const onQueryChange = (q) => {
    setQuery(q);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(q), 300);
  };

  const selectSuggestion = async (s) => {
    setShowDropdown(false);
    setResolving(true);
    try {
      const res = await apiClient.get(`/address/get/${encodeURIComponent(s.id)}`);
      if (res.data?.success && res.data.address) {
        const a = res.data.address;
        const patch = {
          addressLine1: a.addressLine1 || "",
          addressLine2: a.addressLine2 || "",
          townCity: a.townCity || "",
          county: a.county || "",
          postcode: a.postcode || "",
        };
        patch.address = composeAddress(patch);
        onChange(patch);
        setQuery("");
      }
    } catch { /* manual entry remains available */ } finally {
      setResolving(false);
    }
  };

  const setField = (field, val) => {
    const next = { ...value, [field]: val };
    onChange({ [field]: val, address: composeAddress(next) });
  };

  return (
    <div className="space-y-3" data-testid="address-capture">
      {/* Typeahead */}
      <div className="relative">
        <Label className="text-slate-700 font-medium">Find your address</Label>
        <div className="relative mt-1.5">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            value={query}
            onChange={(e) => onQueryChange(e.target.value)}
            onFocus={() => suggestions.length > 0 && setShowDropdown(true)}
            placeholder="Start typing, e.g. 52 Kip..."
            className="h-14 text-base pl-9 border-slate-200"
            autoComplete="off"
            data-testid={`${idPrefix}-typeahead-input`}
          />
          {(searching || resolving) && (
            <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-slate-400" />
          )}
        </div>
        {showDropdown && suggestions.length > 0 && (
          <div className="absolute z-30 mt-1 w-full border border-slate-200 rounded-lg bg-white shadow-lg max-h-56 overflow-y-auto"
               data-testid={`${idPrefix}-suggestions`}>
            {suggestions.map((s, i) => (
              <button key={s.id || i} type="button" onClick={() => selectSuggestion(s)}
                      className="w-full px-4 py-3 text-left text-sm hover:bg-slate-50 border-b border-slate-100 last:border-0 flex items-start gap-2"
                      data-testid={`${idPrefix}-suggestion-${i}`}>
                <MapPin className="h-4 w-4 text-violet-500 mt-0.5 shrink-0" />
                <span>{s.label}</span>
              </button>
            ))}
          </div>
        )}
        <p className="text-xs text-slate-400 mt-1">Or enter the address manually below.</p>
      </div>

      {/* Structured manual fields */}
      <div>
        <Label className="text-slate-700 font-medium">Address Line 1 {required && "*"}</Label>
        <Input value={value.addressLine1 || ""} onChange={(e) => setField("addressLine1", e.target.value)}
               className={`h-12 mt-1.5 text-base ${errors.address ? "border-red-500" : "border-slate-200"}`}
               data-testid={`${idPrefix}-line1`} />
        {errors.address && <p className="text-red-500 text-xs mt-1">{errors.address}</p>}
      </div>
      <div>
        <Label className="text-slate-700 font-medium">Address Line 2</Label>
        <Input value={value.addressLine2 || ""} onChange={(e) => setField("addressLine2", e.target.value)}
               className="h-12 mt-1.5 text-base border-slate-200" data-testid={`${idPrefix}-line2`} />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-slate-700 font-medium">Town/City {required && "*"}</Label>
          <Input value={value.townCity || ""} onChange={(e) => setField("townCity", e.target.value)}
                 className={`h-12 mt-1.5 text-base ${errors.townCity ? "border-red-500" : "border-slate-200"}`}
                 data-testid={`${idPrefix}-town`} />
          {errors.townCity && <p className="text-red-500 text-xs mt-1">{errors.townCity}</p>}
        </div>
        <div>
          <Label className="text-slate-700 font-medium">County</Label>
          <Input value={value.county || ""} onChange={(e) => setField("county", e.target.value)}
                 className="h-12 mt-1.5 text-base border-slate-200" data-testid={`${idPrefix}-county`} />
        </div>
      </div>
      <div>
        <Label className="text-slate-700 font-medium">Postcode {required && "*"}</Label>
        <Input value={value.postcode || ""} onChange={(e) => setField("postcode", e.target.value.toUpperCase())}
               placeholder="SW1A 1AA"
               className={`h-12 mt-1.5 text-base ${errors.postcode ? "border-red-500" : "border-slate-200"}`}
               data-testid={`${idPrefix}-postcode`} />
        {errors.postcode && <p className="text-red-500 text-xs mt-1">{errors.postcode}</p>}
      </div>
    </div>
  );
}
